import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload,
  BookOpen,
  Mic,
  CreditCard,
  Plus,
  Package,
  ExternalLink,
  Copy,
  Globe,
  Search,
  ArrowLeft,
} from "lucide-react";

import publisherService from "@/services/publisherService";
import authService from "@/services/authService";
import paymentService from "@/services/paymentService";
import ebookService from "@/services/ebookService";
import InboxBell from "@/components/shared/InboxBell";
import ThemeToggle from "@/components/shared/ThemeToggle";
import UserMenu from "@/components/shared/UserMenu";

import StatusChip from "@/components/publisher/StatusChip";
import AutosaveIndicator from "@/components/publisher/AutosaveIndicator";
import EbookUploader from "@/components/publisher/EbookUploader";
import EbookAssetCard from "@/components/publisher/EbookAssetCard";
import EbookReaderModal from "@/components/publisher/EbookReaderModal";

const goldButtonClass = "bg-[#c98905] text-white hover:bg-[#a56f00]";
const goldOutlineClass = "border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]";

function PublisherHeader({ navigate, autosaveStatus, lastSaved, profile }) {
  const displayName = profile?.display_name || "Publisher";

  return (
    <header className="sticky top-0 z-50 border-b border-[#eadfcd] bg-white/90 backdrop-blur dark:border-[#2f260f] dark:bg-[#070705]/95">
      <div className="mx-auto max-w-[1800px] px-5 py-4 md:px-8">
        <div className="flex items-center justify-between gap-5">
          <div className="flex items-center gap-8 lg:gap-12">
            <button
              className="flex items-center text-left"
              onClick={() => navigate("/dashboard")}
            >
              <img
                src="/images/golden-records-real-logo.png"
                alt="Golden Records"
                className="h-16 w-56 rounded-lg object-cover object-center"
              />
            </button>

            <nav className="hidden items-center gap-7 lg:flex">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate("/participate")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
              >
                Participate
              </button>
              <button className="border-b-2 border-[#c98905] pb-2 text-sm font-medium text-[#b77900] dark:text-[#f2c95d]">
                Publisher
              </button>
              <button
                onClick={() => navigate("/narrator")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
              >
                Narrator
              </button>
              <button
                onClick={() => navigate("/work")}
                className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
              >
                Work
              </button>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative hidden w-[360px] xl:block">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#c98905]" />
              <Input
                type="text"
                placeholder="Search books, projects, narrators..."
                className="h-11 rounded-lg border-[#eadfcd] bg-white pl-12 text-sm shadow-sm dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] dark:placeholder:text-[#8f8677]"
              />
            </div>

            <ThemeToggle />

            <AutosaveIndicator status={autosaveStatus} lastSaved={lastSaved} />

            <InboxBell />

            <UserMenu displayName={displayName} />


            <Button
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className={`${goldOutlineClass} dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]`}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

function GoldWorkflowShell({ children, navigate, autosaveStatus, lastSaved, profile }) {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#fffaf2] dark:bg-[#050504]">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_86%_8%,rgba(245,207,117,0.24),transparent_25%),linear-gradient(180deg,#ffffff_0%,#fffaf2_42%,#fff1d8_100%)] dark:bg-[radial-gradient(circle_at_82%_10%,rgba(224,165,27,0.30),transparent_22%),linear-gradient(180deg,#050504_0%,#090805_48%,#030302_100%)]" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[62vh] opacity-65 [background:repeating-radial-gradient(ellipse_at_center,rgba(224,165,27,0.18)_0_1px,transparent_1px_18px)] dark:opacity-35" />
      <PublisherHeader
        navigate={navigate}
        autosaveStatus={autosaveStatus}
        lastSaved={lastSaved}
        profile={profile}
      />
      {children}
    </div>
  );
}

function GoldStepper({ steps, currentStep = 0 }) {
  return (
    <div className="mb-7 grid grid-cols-5 items-start gap-3">
      {steps.map((step, index) => {
        const active = index <= currentStep || step.completed;
        return (
          <div key={step.label} className="relative flex flex-col items-start">
            <div className="flex w-full items-center">
              <div
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border text-sm font-bold ${
                  active
                    ? "border-[#c98905] bg-gradient-to-br from-[#f5cf75] to-[#b77900] text-white shadow-[0_8px_20px_rgba(185,119,0,0.22)]"
                    : "border-[#d8bd7a] bg-white text-slate-700"
                }`}
              >
                {index + 1}
              </div>
              {index < steps.length - 1 ? (
                <div className="mx-3 h-0.5 flex-1 bg-[#eadfcd]">
                  <div className={`h-full ${active ? "w-full bg-[#c98905]" : "w-0"}`} />
                </div>
              ) : null}
            </div>
            <p className={`mt-2 text-xs font-semibold ${active ? "text-[#b77900]" : "text-slate-700"}`}>
              {step.label}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function GoldSectionCard({ icon: Icon, label, children, id }) {
  return (
    <Card id={id} className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_18px_55px_rgba(0,0,0,0.42)]">
      <CardContent className="p-6">
        <div className="flex gap-5">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#fff2d8] text-[#c98905]">
            <Icon className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              className="mb-4 text-xl font-semibold text-black dark:text-[#f8efe0]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {label}
            </h2>
            {children}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PublisherWorkflow() {
  const navigate = useNavigate();

  // State
  const [profile, setProfile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [submissionMethod, setSubmissionMethod] = useState("EBOOK_UPLOAD");
  const [narratorType, setNarratorType] = useState("HUMAN");
  const [genderPreference, setGenderPreference] = useState("ANY");
  const [language, setLanguage] = useState("English");
  const [aiVoiceStyle, setAiVoiceStyle] = useState("");

  // Autosave
  const [autosaveStatus, setAutosaveStatus] = useState("idle");
  const [lastSaved, setLastSaved] = useState(null);

  // UI state
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showCreateSubmissionModal, setShowCreateSubmissionModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tracker, setTracker] = useState(null);
  const [creatingSubmission, setCreatingSubmission] = useState(false);
  const [newSubmissionTitle, setNewSubmissionTitle] = useState("");
  const [newSubmissionGenre, setNewSubmissionGenre] = useState("General");

  // eBook upload state
  const [ebookAssets, setEbookAssets] = useState([]);
  const [ebookLoading, setEbookLoading] = useState(false);
  const [readerAsset, setReaderAsset] = useState(null);
  const [showReaderModal, setShowReaderModal] = useState(false);

  // Funding form
  const [fundingPreference, setFundingPreference] = useState("CROWD");
  const [targetAmount, setTargetAmount] = useState("5000");
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Courier form
  const [courierData, setCourierData] = useState({
    full_name: "",
    phone: "",
    email: "",
    pickup_address_line1: "",
    pickup_address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    packaging_ack: false,
    shipping_guidelines_ack: false,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [profileData, submissionsData] = await Promise.all([
        publisherService.getProfile().catch(() => null),
        publisherService.getSubmissions(),
      ]);

      setProfile(profileData || null);
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);

      // Auto-select first submission if none selected yet
      if (
        Array.isArray(submissionsData) &&
        submissionsData.length > 0 &&
        !selectedSubmission
      ) {
        await selectSubmission(submissionsData[0]);
      } else if (!submissionsData || submissionsData.length === 0) {
        setSelectedSubmission(null);
        setTracker(null);
      }
    } catch (error) {
      console.error("Error loading data:", error);

      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        authService.clearTokens();
        navigate("/login");
      } else if (error.response?.status === 404) {
        toast.info("Publisher profile not found. Creating one...");
      } else {
        toast.error("Failed to load publisher data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Load ebooks for a submission
  const loadEbooks = async (submissionId) => {
    if (!submissionId) {
      setEbookAssets([]);
      return;
    }
    
    setEbookLoading(true);
    try {
      const assets = await ebookService.listEbooks(submissionId);
      setEbookAssets(assets || []);
    } catch (error) {
      console.error("Error loading ebooks:", error);
      setEbookAssets([]);
    } finally {
      setEbookLoading(false);
    }
  };

  const selectSubmission = async (submission) => {
    if (!submission) return;

    setSelectedSubmission(submission);
    setSubmissionMethod(submission.submission_type || "EBOOK_UPLOAD");
    setNarratorType(submission.narrator_type || "HUMAN");
    setGenderPreference(submission.gender_preference || "ANY");
    setLanguage(submission.language || "English");
    setAiVoiceStyle(submission.ai_voice_style || "");

    // Load tracker
    try {
      const trackerData = await publisherService.getSubmissionTracker(
        submission.id
      );
      setTracker(trackerData);
    } catch (error) {
      console.error("Error loading tracker:", error);
      setTracker(null);
    }

    // Load ebooks
    await loadEbooks(submission.id);
  };

  // Handle ebook upload complete
  const handleEbookUploadComplete = async (newAsset) => {
    // Refresh ebook list
    await loadEbooks(selectedSubmission?.id);
    // Refresh submission data to update ebook_count
    await loadData();
  };

  // Handle ebook delete
  const handleEbookDelete = async (assetId) => {
    setEbookAssets(prev => prev.filter(a => a.id !== assetId));
    // Refresh submission data to update ebook_count
    await loadData();
  };

  // Open reader modal
  const handleOpenReader = (asset) => {
    setReaderAsset(asset);
    setShowReaderModal(true);
  };

  // Autosave debounced update
  const debouncedUpdate = useCallback(
    debounce(async (submissionId, updateData) => {
      try {
        setAutosaveStatus("saving");
        await publisherService.updateSubmission(submissionId, updateData);
        setAutosaveStatus("saved");
        setLastSaved(Date.now());
      } catch (error) {
        console.error("Autosave error:", error);
        setAutosaveStatus("error");
      }
    }, 800),
    []
  );

  const handleFieldUpdate = (field, value) => {
    if (!selectedSubmission) return;

    setSelectedSubmission((prev) => ({ ...prev, [field]: value }));
    debouncedUpdate(selectedSubmission.id, { [field]: value });
  };

  const handleCreateSubmission = () => {
    setNewSubmissionTitle("");
    setNewSubmissionGenre("General");
    setShowCreateSubmissionModal(true);
  };

  const handleConfirmCreateSubmission = async () => {
    const title = newSubmissionTitle.trim();

    if (title.length < 3) {
      toast.error("Please enter a submission name");
      return;
    }

    try {
      setCreatingSubmission(true);
      const result = await publisherService.createSubmission({
        title,
        genre: newSubmissionGenre.trim() || "General",
        language: language,
        submission_type: submissionMethod,
      });

      toast.success("Submission created");
      setShowCreateSubmissionModal(false);

      const submissionsData = await publisherService.getSubmissions();
      const nextSubmissions = Array.isArray(submissionsData) ? submissionsData : [];
      setSubmissions(nextSubmissions);

      const createdSubmission =
        nextSubmissions.find((sub) => sub.id === result.id) || nextSubmissions[0];
      if (createdSubmission) {
        await selectSubmission(createdSubmission);
      }
    } catch (error) {
      console.error("Error creating submission:", error);
      toast.error("Failed to create submission");
    } finally {
      setCreatingSubmission(false);
    }
  };

  const handleSaveCourier = async () => {
    if (!selectedSubmission) return;

    try {
      await publisherService.addCourierRequest(
        selectedSubmission.id,
        courierData
      );
      toast.success("Courier request saved");
      await loadData();
    } catch (error) {
      console.error("Error saving courier:", error);
      toast.error("Failed to save courier request");
    }
  };

  const handleSubmitForReview = async () => {
    if (!selectedSubmission) return;

    try {
      await publisherService.submitForReview(selectedSubmission.id);
      toast.success("Submission sent for review");
      await loadData();
    } catch (error) {
      console.error("Error submitting:", error);
      if (error.response?.data?.detail?.errors) {
        const errors = error.response.data.detail.errors;
        toast.error(`Cannot submit: ${errors.join(", ")}`);
      } else {
        toast.error("Failed to submit for review");
      }
    }
  };

  const handleRequestFunding = async () => {
    if (!selectedSubmission) {
      toast.error("Please select a submission first");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept the terms and conditions");
      return;
    }

    try {
      const result = await publisherService.requestFunding(
        selectedSubmission.id,
        {
          funding_preference: fundingPreference,
          target_amount: parseFloat(targetAmount),
          currency: "USD",
          terms_version: "v1.0",
          accepted: termsAccepted,
        }
      );

      toast.success(result.message || "Funding requested");
      setShowFundingModal(false);
      await loadData();
    } catch (error) {
      console.error("Error requesting funding:", error);
      toast.error("Failed to request funding");
    }
  };

  // ✅ NEW: Payment button action (Opens PaymentEntry page)
  const handlePublisherPayment = () => {
    if (!selectedSubmission) {
      toast.error("Please select a submission first");
      return;
    }

    const paymentUrl = paymentService.openPayment({
      purpose: "INDIVIDUAL_FUNDING",
      entityType: "SUBMISSION",
      entityId: selectedSubmission.id,
      suggestedAmount: parseFloat(targetAmount) || 5000,
      currency: "USD",
      allowEncryption: true,
      returnUrl: "/publisher/workflow",
    });

    navigate(paymentUrl);
  };

  // Step completion check - uses ebookAssets for real-time accuracy
  const getStepCompletion = () => {
    const defaultSteps = [
      { label: "Upload", completed: false },
      { label: "Language", completed: false },
      { label: "Narrator", completed: false },
      { label: "Book Info", completed: false },
      { label: "Funding", completed: false },
    ];

    if (!selectedSubmission) {
      return { steps: defaultSteps, completedCount: 0 };
    }

    // Step 1: Use ebookAssets for real-time check, fallback to ebook_count
    const hasEbook = ebookAssets.length > 0 || (selectedSubmission.ebook_count || 0) > 0;
    const step1Complete =
      (submissionMethod === "EBOOK_UPLOAD" && hasEbook) ||
      (submissionMethod === "HARDCOPY_COURIER" &&
        !!selectedSubmission.has_courier_request);

    const step2Complete = !!selectedSubmission.language;
    const step3Complete =
      !!selectedSubmission.narrator_type &&
      !!selectedSubmission.gender_preference;
    const step4Complete =
      !!selectedSubmission.title && selectedSubmission.title.length > 3;
    const step5Complete = !!selectedSubmission.needs_funding;

    const steps = [
      { label: "Upload", completed: step1Complete },
      { label: "Language", completed: step2Complete },
      { label: "Narrator", completed: step3Complete },
      { label: "Book Info", completed: step4Complete },
      { label: "Funding", completed: step5Complete },
    ];

    return {
      steps,
      completedCount: steps.filter((s) => s.completed).length,
    };
  };

  const { steps, completedCount } = getStepCompletion();

  if (loading) {
    return (
      <GoldWorkflowShell
        navigate={navigate}
        autosaveStatus={autosaveStatus}
        lastSaved={lastSaved}
        profile={profile}
      >
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b77900] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading workflow...</p>
        </div>
      </div>
      </GoldWorkflowShell>
    );
  }

  return (
    <GoldWorkflowShell
      navigate={navigate}
      autosaveStatus={autosaveStatus}
      lastSaved={lastSaved}
      profile={profile}
    >
      {/* Main Content */}
      <main className="relative z-10 mx-auto max-w-[1640px] px-5 py-8 md:px-8">
        <div className="mb-7">
          <h2
            className="mb-2 text-4xl font-bold text-slate-950 dark:text-[#f8efe0] md:text-5xl"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Publisher Workflow
          </h2>
          <p className="text-slate-600 dark:text-[#d5c8ae]">
            Submit your book for narration and preservation
          </p>
        </div>

        {/* Progress Indicator */}
        {selectedSubmission && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">
                {completedCount} of 5 steps complete
              </span>
              <span className="text-sm text-slate-600">
                {Math.round((completedCount / 5) * 100)}%
              </span>
            </div>

            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#b77900] to-[#f5cf75] transition-all duration-500"
                style={{ width: `${(completedCount / 5) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        <GoldStepper steps={steps} currentStep={0} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1 */}
            <div id="step-1-upload">
              <GoldSectionCard
                id="step-1-upload"
                icon={Upload}
                label="Step 1: Upload eBook or Request Courier"
              >
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Button
                      onClick={() => setSubmissionMethod("EBOOK_UPLOAD")}
                      variant={
                        submissionMethod === "EBOOK_UPLOAD"
                          ? "default"
                          : "outline"
                      }
                      className={
                        submissionMethod === "EBOOK_UPLOAD"
                          ? goldButtonClass
                          : goldOutlineClass
                      }
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload eBook
                    </Button>

                    <Button
                      onClick={() => setSubmissionMethod("HARDCOPY_COURIER")}
                      variant={
                        submissionMethod === "HARDCOPY_COURIER"
                          ? "default"
                          : "outline"
                      }
                      className={
                        submissionMethod === "HARDCOPY_COURIER"
                          ? goldButtonClass
                          : goldOutlineClass
                      }
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Courier Hardcopy
                    </Button>
                  </div>

                  {submissionMethod === "EBOOK_UPLOAD" && (
                    <div className="space-y-4">
                      {/* Show existing ebook assets */}
                      {ebookLoading ? (
                        <div className="p-6 text-center text-slate-500">
                          Loading files...
                        </div>
                      ) : ebookAssets.length > 0 ? (
                        <div className="space-y-3">
                          {ebookAssets.map((asset) => (
                            <EbookAssetCard
                              key={asset.id}
                              asset={asset}
                              submissionId={selectedSubmission?.id}
                              onDelete={handleEbookDelete}
                              onOpenReader={handleOpenReader}
                              onReplace={() => {
                                // Scroll to uploader or show replace mode
                              }}
                            />
                          ))}
                        </div>
                      ) : null}

                      {/* Uploader */}
                      <EbookUploader
                        submissionId={selectedSubmission?.id}
                        onUploadComplete={handleEbookUploadComplete}
                        disabled={!selectedSubmission}
                      />

                      {!selectedSubmission && (
                        <p className="text-sm text-amber-600 text-center">
                          Please select or create a submission first (Step 4)
                        </p>
                      )}
                    </div>
                  )}

                  {submissionMethod === "HARDCOPY_COURIER" && (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700 font-medium">
                        Courier pickup address (stub)
                      </p>
                      <Input
                        placeholder="Full name"
                        value={courierData.full_name}
                        onChange={(e) =>
                          setCourierData({
                            ...courierData,
                            full_name: e.target.value,
                          })
                        }
                      />
                      <Button onClick={handleSaveCourier} className="w-full">
                        Save Courier Request
                      </Button>
                    </div>
                  )}
                </div>
              </GoldSectionCard>
            </div>

            {/* Step 2 */}
            <div id="step-2-language">
              <GoldSectionCard
                id="step-2-language"
                icon={Globe}
                label="Step 2: Select Language"
              >
                <div className="space-y-2">
                  <Label>Book Language</Label>
                  <Input
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      handleFieldUpdate("language", e.target.value);
                    }}
                    placeholder="e.g., English, Spanish, French"
                    className="border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                  />
                </div>
              </GoldSectionCard>
            </div>

            {/* Step 3 */}
            <div id="step-3-narrator">
              <GoldSectionCard
                id="step-3-narrator"
                icon={Mic}
                label="Step 3: Choose Narrator Type"
              >
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Button
                      onClick={() => {
                        setNarratorType("HUMAN");
                        handleFieldUpdate("narrator_type", "HUMAN");
                      }}
                      variant={narratorType === "HUMAN" ? "default" : "outline"}
                      className={
                        narratorType === "HUMAN"
                          ? goldButtonClass
                          : goldOutlineClass
                      }
                    >
                      Human
                    </Button>

                    <Button
                      onClick={() => {
                        setNarratorType("AI");
                        handleFieldUpdate("narrator_type", "AI");
                      }}
                      variant={narratorType === "AI" ? "default" : "outline"}
                      className={
                        narratorType === "AI"
                          ? goldButtonClass
                          : goldOutlineClass
                      }
                    >
                      AI Voice
                    </Button>
                  </div>

                  <div>
                    <Label>Gender Preference</Label>
                    <div className="flex gap-2 mt-2">
                      {["MALE", "FEMALE", "ANY"].map((gender) => (
                        <Button
                          key={gender}
                          size="sm"
                          onClick={() => {
                            setGenderPreference(gender);
                            handleFieldUpdate("gender_preference", gender);
                          }}
                          variant={
                            genderPreference === gender ? "default" : "outline"
                          }
                          className={
                            genderPreference === gender
                              ? goldButtonClass
                              : goldOutlineClass
                          }
                        >
                          {gender}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {narratorType === "AI" && (
                    <div>
                      <Label>AI Voice Style (Optional)</Label>
                      <Input
                        value={aiVoiceStyle}
                        onChange={(e) => {
                          setAiVoiceStyle(e.target.value);
                          handleFieldUpdate("ai_voice_style", e.target.value);
                        }}
                        placeholder="e.g., Natural, Clear, Expressive"
                        className="border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                      />
                    </div>
                  )}
                </div>
              </GoldSectionCard>
            </div>

            {/* Step 4 */}
            <div id="step-4-select">
              <GoldSectionCard
                id="step-4-select"
                icon={BookOpen}
                label="Step 4: Manage Submissions"
              >
                <div className="space-y-4">
                  <Button
                    onClick={handleCreateSubmission}
                    className={`w-full ${goldOutlineClass}`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Submission
                  </Button>

                  <div className="space-y-2">
                    {submissions.map((sub) => (
                      <div
                        key={sub.id}
                        onClick={() => selectSubmission(sub)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedSubmission?.id === sub.id
                            ? "border-[#d8bd7a] bg-[#fff8eb]"
                            : "border-[#eadfcd] hover:border-[#d8bd7a] bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-black">
                              {sub.title}
                            </h4>
                            <p className="text-sm text-slate-600">
                              {sub.language} • {sub.genre}
                            </p>

                            <div className="flex gap-2 mt-2">
                              <StatusChip status={sub.submission_status} />

                              {sub.needs_funding && (
                                <Badge className="bg-[#fff2d8] text-[#9a6500]">
                                  Funding
                                </Badge>
                              )}

                              <Badge className="bg-slate-100 text-slate-800">
                                Docs: {sub.doc_completeness_score}%
                              </Badge>
                            </div>
                          </div>

                          <Button
                            size="sm"
                            variant="outline"
                            className={goldOutlineClass}
                            onClick={(e) => {
                              e.stopPropagation();
                              selectSubmission(sub);
                            }}
                          >
                            Select
                          </Button>
                        </div>
                      </div>
                    ))}

                    {submissions.length === 0 && (
                      <div className="rounded-lg border border-[#eadfcd] bg-[#fffaf2] p-8 text-center">
                        <div className="mx-auto mb-3 grid h-20 w-20 place-items-center rounded-full bg-[#fff2d8] text-[#c98905]">
                          <BookOpen className="h-10 w-10" />
                        </div>
                        <p className="text-slate-700 font-medium">
                          No submissions yet
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Create your first submission to begin.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </GoldSectionCard>
            </div>

            {/* Step 5 */}
            <div id="step-5-funding">
              <GoldSectionCard
                id="step-5-funding"
                icon={CreditCard}
                label="Step 5: Payment & Funding"
              >
                <div className="space-y-4">
                  {/* ✅ NOW WORKING PAYMENT BUTTON */}
                  <Button
                    onClick={handlePublisherPayment}
                    disabled={!selectedSubmission}
                    className={`w-full ${goldOutlineClass}`}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Process Payment
                  </Button>

                  <Button
                    onClick={() => setShowFundingModal(true)}
                    variant="outline"
                    className="w-full border-[#eadfcd] bg-white text-slate-700 hover:bg-[#fff8eb]"
                    disabled={!selectedSubmission}
                  >
                    Request Funding Activation
                  </Button>
                </div>
              </GoldSectionCard>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {selectedSubmission && (
              <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)]">
                <CardHeader>
                  <CardTitle
                    className="text-lg"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    Current Submission
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-600">Title</p>
                    <p className="font-semibold text-black">
                      {selectedSubmission.title}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-600">Status</p>
                    <StatusChip status={selectedSubmission.submission_status} />
                  </div>

                  <div>
                    <p className="text-sm text-slate-600">Language</p>
                    <p className="text-black">{selectedSubmission.language}</p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-600">Narrator</p>
                    <p className="text-black">
                      {selectedSubmission.narrator_type} •{" "}
                      {selectedSubmission.gender_preference}
                    </p>
                  </div>

                  {selectedSubmission.submission_status === "DRAFT" && (
                    <Button
                      onClick={handleSubmitForReview}
                      className={`w-full ${goldButtonClass}`}
                    >
                      Submit for Review
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {profile && (
              <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)]">
                <CardHeader>
                  <CardTitle
                    className="text-lg"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    Public Profile
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-3">
                  <p className="font-semibold text-black">
                    {profile.display_name}
                  </p>

                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-white px-2 py-1 rounded">
                      /p/{profile.slug}
                    </code>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/p/${profile.slug}`
                        );
                        toast.success("Link copied");
                      }}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(`/p/${profile.slug}`, "_blank")}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Create Submission Modal */}
      <Dialog
        open={showCreateSubmissionModal}
        onOpenChange={setShowCreateSubmissionModal}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Create New Submission</DialogTitle>
            <DialogDescription>
              Name this book submission before uploading the ebook.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="submission-title">Submission Name</Label>
              <Input
                id="submission-title"
                value={newSubmissionTitle}
                onChange={(e) => setNewSubmissionTitle(e.target.value)}
                placeholder="e.g., The Golden Manuscript"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="submission-genre">Genre</Label>
              <Input
                id="submission-genre"
                value={newSubmissionGenre}
                onChange={(e) => setNewSubmissionGenre(e.target.value)}
                placeholder="General"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateSubmissionModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmCreateSubmission}
                disabled={creatingSubmission || newSubmissionTitle.trim().length < 3}
                className={`flex-1 ${goldButtonClass}`}
              >
                {creatingSubmission ? "Creating..." : "Create Submission"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Funding Modal */}
      <Dialog open={showFundingModal} onOpenChange={setShowFundingModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Request Funding Activation</DialogTitle>
            <DialogDescription>
              Set your funding preferences for this book
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Funding Preference</Label>
              <div className="flex gap-2 mt-2">
                {["CROWD", "INDIVIDUAL", "ANY"].map((pref) => (
                  <Button
                    key={pref}
                    size="sm"
                    onClick={() => setFundingPreference(pref)}
                    variant={fundingPreference === pref ? "default" : "outline"}
                  >
                    {pref}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label>Target Amount (USD)</Label>
              <Input
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                min="100"
              />
            </div>

            <div className="p-4 bg-slate-50 rounded-lg max-h-32 overflow-y-auto text-xs text-slate-600">
              <p className="font-semibold mb-2">Terms & Conditions</p>
              <p>
                By requesting funding, you agree to allow your book to be listed
                on the funding platform...
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="terms" className="text-sm text-slate-700">
                I accept the terms and conditions
              </label>
            </div>

            <Button
              onClick={handleRequestFunding}
              disabled={!termsAccepted}
              className={`w-full ${goldButtonClass}`}
            >
              Submit Funding Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* eBook Reader Modal */}
      <EbookReaderModal
        isOpen={showReaderModal}
        onClose={() => {
          setShowReaderModal(false);
          setReaderAsset(null);
        }}
        asset={readerAsset}
      />
    </GoldWorkflowShell>
  );
}

// Debounce utility
function debounce(func, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}
